import React, {
  type FC,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Flex, type UseDisclosureProps, useDisclosure } from '@chakra-ui/react';

import { Viewport } from '@/components/Viewport';
import { InterviewNavBar } from '@/features/interview';
import { MediaDeviceProvider } from '@/features/media';
import useDisableRightClickAndShortcuts from '@/utils/hooks/useBlockRightClick';

export type InterviewLayoutContextNavDisplayed = boolean;

type InterviewLayoutContextValue = {
  navDisplayed: InterviewLayoutContextNavDisplayed;
  setNavDisplayed: React.Dispatch<
    React.SetStateAction<InterviewLayoutContextNavDisplayed>
  >;
  navDrawer: UseDisclosureProps;
  isPublicOnly: boolean;
  disableLangChange: boolean;
  setDisableLangChange: React.Dispatch<React.SetStateAction<boolean>>;
};

export const InterviewLayoutContext =
  React.createContext<InterviewLayoutContextValue | null>(null);

export const useInterviewLayoutContext = () => {
  const ctx = useContext(InterviewLayoutContext);
  if (ctx === null) {
    throw new Error('Missing parent <InterviewLayout> component');
  }
  return ctx;
};

export const useInterviewLayoutHideNav = (
  displayed: InterviewLayoutContextNavDisplayed = true
) => {
  const { setNavDisplayed } = useInterviewLayoutContext();

  useEffect(() => {
    setNavDisplayed(displayed);
    return () => setNavDisplayed(true);
  }, [setNavDisplayed, displayed]);
};

export const InterviewLayout: FC<
  React.PropsWithChildren<{
    isPublicOnly?: boolean;
  }>
> = ({ isPublicOnly = false, children }) => {
  const [navDisplayed, setNavDisplayed] =
    useState<InterviewLayoutContextNavDisplayed>(true);
  const [disableLangChange, setDisableLangChange] = useState<boolean>(false);
  const navDrawer = useDisclosure();
  useDisableRightClickAndShortcuts();

  const providerValue = useMemo(
    () =>
      ({
        navDisplayed,
        setNavDisplayed,
        navDrawer,
        isPublicOnly,
        disableLangChange,
        setDisableLangChange,
      }) satisfies InterviewLayoutContextValue,
    [navDisplayed, navDrawer, isPublicOnly, disableLangChange]
  );

  return (
    <InterviewLayoutContext.Provider value={providerValue}>
      <Viewport bgGradient="linear(to-b, brand.25, gray.50)">
        {!!navDisplayed && <InterviewNavBar display="block" />}
        <Flex flex="1" direction="column">
          <MediaDeviceProvider>{children}</MediaDeviceProvider>
        </Flex>
      </Viewport>
    </InterviewLayoutContext.Provider>
  );
};
